import {
  AgxBadge,
  AgxButton,
  AgxEmptyState,
  AgxSpacer,
  AgxTable,
  AgxTypography,
  IColumnConfig,
  Icon_Chevron,
  SelectChipOption,
} from '@urbanx/agx.ui-components2'
import { useCallback, useMemo, useRef } from 'react'
import { TaskCell } from 'pages/allTasks/Cells/TaskCell'
import useTaskSearch from 'pages/allTasks/useTaskSearch'
import TaskAgencyCell from 'pages/allTasks/Cells/TaskAgencyCell'
import TaskCampaignCell from 'pages/allTasks/Cells/TaskCampaignCell'
import TaskAssigneeCell from 'pages/allTasks/Cells/TaskAssigneeCell'
import {
  type AutopilotTaskDto,
  useAssignTaskToUser,
} from '@urbanx/agx.activities.client'
import TaskStatusCell from 'pages/allTasks/Cells/TaskStatusCell'
import TaskDueCell from 'pages/allTasks/Cells/TaskDueCell'
import { useNavigateToTask } from 'utils/tasks/useNavigateToTask'
import { AgencyModel } from '@urbanx/agx.agencies.client'
import { useNavigate } from 'react-router-dom'
import { Pages } from 'constants/menu'
import { createTodaysTasksFilterState } from './createTodaysTasksFilterState'
import './TodaysTasks.scss'
import { useWorkflowConcurrencyError } from 'hooks/useWorkflowConcurrencyError'

interface TodaysTasksProps {
  isLoading?: boolean
  todaysTasksCount?: number
  assignedToUserIds?: string[]
  selectedAgencies?: string[]
  assigneeOptions: SelectChipOption[]
  assigneeOptionsPending?: boolean
  agencies: AgencyModel[]
  isAgencyOptionsPending?: boolean
}

const tableColumns: (string | IColumnConfig)[] = [
  'Task',
  'Agency',
  'Campaign',
  {
    name: 'Assignee',
    align: 'center',
    onClick: (e: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>) =>
      e.stopPropagation(),
  },
  'Status',
  { name: 'Due', align: 'right' },
]

const TodaysTasks = ({
  isLoading,
  todaysTasksCount,
  assignedToUserIds,
  selectedAgencies,
  assigneeOptions,
  assigneeOptionsPending,
  agencies,
  isAgencyOptionsPending,
}: TodaysTasksProps) => {
  const assignmentWorkflowRef = useRef<AutopilotTaskDto | undefined>(undefined)
  const handleWorkflowConcurrencyError = useWorkflowConcurrencyError()
  const { tasks, searchAllTasksPending, refreshSearch } = useTaskSearch(
    createTodaysTasksFilterState(assignedToUserIds, selectedAgencies),
    isLoading
  )
  const { assignTaskToUser, assignTaskToUserPending } = useAssignTaskToUser({
    onSuccess: () => {},
    onError: (error) => {
      if (!assignmentWorkflowRef.current) return

      void handleWorkflowConcurrencyError(
        error,
        assignmentWorkflowRef.current,
        { refresh: refreshSearch }
      )
    },
  })

  const onAssignTaskToUser = useCallback(
    (userId?: string, taskId?: string) => {
      if (!userId || !taskId || assignTaskToUserPending) return

      assignmentWorkflowRef.current = tasks.find((task) => task.id === taskId)

      assignTaskToUser({
        taskId,
        userId,
      })
    },
    [assignTaskToUserPending, assignTaskToUser, tasks]
  )

  const anythingPending = useMemo(() => {
    return (
      searchAllTasksPending ||
      isLoading ||
      isAgencyOptionsPending ||
      assigneeOptionsPending
    )
  }, [
    searchAllTasksPending,
    isLoading,
    isAgencyOptionsPending,
    assigneeOptionsPending,
  ])

  const rows = useMemo(() => {
    if (anythingPending) return []

    return tasks.map((task, idx) => {
      const isNew = !!task.isNew

      return {
        Task: <TaskCell task={task} index={idx} isNew={isNew} />,
        Agency: (
          <TaskAgencyCell
            task={task}
            agencies={agencies ?? []}
            index={idx}
            isNew={isNew}
          />
        ),
        Campaign: (
          <TaskCampaignCell
            propertyAddress={task.propertyAddress ?? 'Unknown Address'}
            index={idx}
            isNew={isNew}
          />
        ),
        Assignee: (
          <TaskAssigneeCell
            task={task}
            assigneeOptions={assigneeOptions}
            onAssignTask={onAssignTaskToUser}
          />
        ),
        Status: <TaskStatusCell task={task} />,
        Due: <TaskDueCell task={task} />,
      }
    })
  }, [anythingPending, tasks, agencies, assigneeOptions])

  const navigateToTask = useNavigateToTask()

  const onTaskRowClicked = useCallback(
    (rowIndex: number) => {
      if (!tasks || rowIndex < 0 || rowIndex >= tasks.length) return

      const task = tasks[rowIndex]

      navigateToTask(task)
    },
    [tasks, navigateToTask]
  )

  const navigate = useNavigate()

  const onSeeAllTasksClick = useCallback(() => {
    navigate(`/${Pages.ALL_TASKS}`)
  }, [navigate])

  return (
    <AgxSpacer direction="vertical" fill gap="sm">
      <AgxSpacer
        direction="horizontal"
        contentJustification="space-between"
        contentAlignment="center"
        fill
      >
        <AgxSpacer
          direction="horizontal"
          contentAlignment="center"
          gap="spacing-md"
        >
          <AgxTypography
            id={'todays-tasks-title'}
            type={'body_ui_strong'}
            size={'medium'}
          >
            {"Today's Tasks"}
          </AgxTypography>
          <AgxBadge
            type="colourfill"
            size="sm"
            colour="grey"
            text={todaysTasksCount?.toString() ?? '--'}
          />
        </AgxSpacer>
        <AgxButton
          variant="linkBrand"
          size="small"
          rightIcon={
            <Icon_Chevron direction={'right'} width={14} height={14} />
          }
          id={'todays-tasks-view-all-button'}
          text={'See all tasks'}
          onClick={onSeeAllTasksClick}
        />
      </AgxSpacer>

      {tasks.length >= 0 || searchAllTasksPending ? (
        <div className="todaysTasksTable">
          <AgxTable
            variant="default"
            tableStyle="rounded"
            columns={tableColumns}
            rows={rows}
            showLoadingState={anythingPending}
            onRowClick={onTaskRowClicked}
            noDataGlyphType="checklistSmile"
            noDataLabel="No task found."
            bodyBg="var(--bg-secondary)"
          />
        </div>
      ) : null}
      {tasks.length === 0 && !searchAllTasksPending && (
        <AgxEmptyState label="No tasks found" variant="minimal" />
      )}
    </AgxSpacer>
  )
}

export default TodaysTasks
